import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { LocationTracker } from "./components/LocationTracker";
import { MobileBottomNav } from "./components/layout/MobileBottomNav";
import { PWAInstallPrompt } from "./components/PWAInstallPrompt";
import { OfflineIndicator } from "./components/OfflineIndicator";
import { Toaster } from "./components/ui/sonner";
import { UpgradePrompt } from "./components/UpgradePrompt";
import { subscriptionApi } from "./lib/api";
import { useState, useEffect } from "react";
import "@/App.css";

// Pages
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import DashboardPage from "./pages/DashboardPage";
import TasksPage from "./pages/TasksPage";
import TaskDetailPage from "./pages/TaskDetailPage";
import TeamPage from "./pages/TeamPage";
import MyTasksPage from "./pages/MyTasksPage";
import AIAssistantPage from "./pages/AIAssistantPage";
import PricingPage from "./pages/PricingPage";
import UsersPage from "./pages/UsersPage";
import CustomersPage from "./pages/CustomersPage";
import ActivitiesPage from "./pages/ActivitiesPage";
import SubscriptionPage from "./pages/SubscriptionPage";
import CalendarPage from "./pages/CalendarPage";
import IntegrationsPage from "./pages/IntegrationsPage";
import LandingPage from "./pages/LandingPage";
import AuditLogsPage from "./pages/AuditLogsPage";
import LiveTrackingPage from "./pages/LiveTrackingPage";
import VisitOptimizerPage from "./pages/VisitOptimizerPage";

// Protected Route wrapper
function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Owners bypass all role checks
    if (user.role === 'owner') return children;
    // Redirect based on role
    if (user.role === 'salesperson') {
      return <Navigate to="/my-tasks" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

// Public Route wrapper (redirect if authenticated)
function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (user) {
    if (user.role === 'salesperson') {
      return <Navigate to="/my-tasks" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

// Landing Route - always shows landing page
function LandingRoute() {
  return <LandingPage />;
}

// Tier-gated route wrapper — checks if user's tier includes the required feature
function TierGate({ children, requiredFeature, featureLabel, requiredTier }) {
  const { user, isOwner } = useAuth();
  const [tierData, setTierData] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (isOwner) {
      setChecking(false);
      return;
    }
    subscriptionApi.getMyTier()
      .then(res => setTierData(res.data))
      .catch(() => {})
      .finally(() => setChecking(false));
  }, [isOwner]);

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Owners bypass all tier gates
  if (isOwner) return children;

  const features = tierData?.features || [];
  if (requiredFeature && !features.includes(requiredFeature)) {
    return (
      <UpgradePrompt
        feature={featureLabel || requiredFeature}
        currentTier={tierData?.tier || 'starter'}
        requiredTier={requiredTier}
      />
    );
  }

  return children;
}

import { usePushNotifications } from "./hooks/usePushNotifications";

function AppRoutes() {
  usePushNotifications();
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />

      {/* Admin/Manager/Salesperson Dashboard */}
      <Route path="/dashboard" element={
        <ProtectedRoute allowedRoles={['admin', 'manager', 'salesperson']}>
          <DashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/tasks" element={
        <ProtectedRoute allowedRoles={['admin', 'manager']}>
          <TasksPage />
        </ProtectedRoute>
      } />
      <Route path="/tasks/:id" element={
        <ProtectedRoute>
          <TaskDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/team" element={
        <ProtectedRoute allowedRoles={['admin', 'manager']}>
          <TeamPage />
        </ProtectedRoute>
      } />
      <Route path="/users" element={
        <ProtectedRoute allowedRoles={['admin', 'manager']}>
          <UsersPage />
        </ProtectedRoute>
      } />
      <Route path="/customers" element={
        <ProtectedRoute>
          <CustomersPage />
        </ProtectedRoute>
      } />
      <Route path="/activities" element={
        <ProtectedRoute>
          <ActivitiesPage />
        </ProtectedRoute>
      } />

      {/* Salesperson Routes */}
      <Route path="/my-tasks" element={
        <ProtectedRoute allowedRoles={['salesperson']}>
          <MyTasksPage />
        </ProtectedRoute>
      } />

      {/* Shared Routes */}
      <Route path="/live-tracking" element={
        <ProtectedRoute allowedRoles={['admin', 'manager', 'owner']}>
          <TierGate requiredFeature="gps_live_tracking" featureLabel="Live GPS Tracking" requiredTier="professional">
            <LiveTrackingPage />
          </TierGate>
        </ProtectedRoute>
      } />
      <Route path="/visit-optimizer" element={
        <ProtectedRoute>
          <VisitOptimizerPage />
        </ProtectedRoute>
      } />
      <Route path="/ai-assistant" element={
        <ProtectedRoute>
          <AIAssistantPage />
        </ProtectedRoute>
      } />
      <Route path="/pricing" element={
        <ProtectedRoute>
          <PricingPage />
        </ProtectedRoute>
      } />
      <Route path="/subscription" element={
        <ProtectedRoute>
          <SubscriptionPage />
        </ProtectedRoute>
      } />
      <Route path="/subscription/success" element={
        <ProtectedRoute>
          <SubscriptionPage />
        </ProtectedRoute>
      } />
      <Route path="/calendar" element={
        <ProtectedRoute>
          <CalendarPage />
        </ProtectedRoute>
      } />
      <Route path="/integrations" element={
        <ProtectedRoute allowedRoles={['admin', 'manager']}>
          <TierGate requiredFeature="crm_integrations" featureLabel="CRM Integrations" requiredTier="enterprise">
            <IntegrationsPage />
          </TierGate>
        </ProtectedRoute>
      } />
      <Route path="/audit-logs" element={
        <ProtectedRoute allowedRoles={['admin', 'manager']}>
          <AuditLogsPage />
        </ProtectedRoute>
      } />

      {/* Default redirect */}
      <Route path="/" element={<LandingRoute />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <OfflineIndicator />
        <LocationTracker />
        <PWAInstallPrompt />
        <AppRoutes />
        <MobileBottomNav />
        <Toaster richColors position="top-right" />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
